const geometry = require("../maps/geometry");

const originShift = (2.0 * Math.PI * 6378137.0) / 2.0;
const initialResolution = (2.0 * Math.PI * 6378137.0) / 256.0;

const UNITS = {
    meter: "meter",
    pixel: "pixel",
};

/**
 *
 * @param meterPoint
 * @param resolution
 * @returns {*}
 * @private
 */
function _toPixel(meterPoint, resolution) {
    return new geometry.Point((meterPoint.x + originShift) / resolution, (meterPoint.y + originShift) / resolution);
}

/**
 *
 * @param pixelPoint
 * @param resolution
 * @returns {*}
 * @private
 */
function _toMeter(pixelPoint, resolution) {
    return new geometry.Point(pixelPoint.x * resolution - originShift, pixelPoint.y * resolution - originShift);
}

/**
 *
 * @param point
 * @returns {*}
 * @private
 */
function _toLatLng(point) {
    const lng = (point.x / originShift) * 180.0;
    let lat = (point.y / originShift) * 180.0;

    lat = (180 / Math.PI) * (2 * Math.atan(Math.exp((lat * Math.PI) / 180.0)) - Math.PI / 2.0);

    return new geometry.LatLng(lat, lng);
}

/**
 *
 * @param {woosmap.geometry.LatLng} latLng
 * @returns {*}
 * @private
 */
function _fromLatLng(latLng) {
    const coordinates = new geometry.LatLng(latLng.lat, latLng.lng);

    const mx = (coordinates.lng * originShift) / 180.0;
    let my = Math.log(Math.tan(((90 + coordinates.lat) * Math.PI) / 360.0)) / (Math.PI / 180.0);

    my = (my * originShift) / 180.0;

    return new geometry.Point(mx, my);
}

/**
 * Constructs SphericalBounds instance from LatLngBounds instance.
 * @param {woosmap.geometry.LatLngBounds} latLngBounds
 */
function fromLatLngBounds(latLngBounds) {
    const retVal = new SphericalBounds(
        _fromLatLng(latLngBounds.getSouthWest()),
        _fromLatLng(latLngBounds.getNorthEast()),
    );

    retVal.unit = UNITS.meter;

    return retVal;
}

const converters = {
    fromLatLng: _fromLatLng,
    toLatLng: _toLatLng,
    toMeter: _toMeter,
    toPixel: _toPixel,
};

class SphericalBounds {
    /**
     * Constructs a SphericalBounds object
     * @param {woosmap.maps.geometry.Point} sw
     * @param {woosmap.maps.geometry.Point} ne
     * @private
     * @constructor
     */
    constructor(sw, ne) {
        this.sw = sw;
        this.ne = ne;
        this.unit = UNITS.meter;
    }

    /**
     *
     * @param zoom
     * @param lambda
     * @param inner
     * @returns {SphericalBounds}
     * @private
     */
    _convert(zoom, lambda, inner) {
        if (this.unit === inner) {
            throw `These bounds are already in ${inner}`;
        }

        const resolution = initialResolution / 2 ** zoom;
        const retVal = new SphericalBounds(lambda(this.sw, resolution), lambda(this.ne, resolution));

        retVal.unit = inner;
        return retVal;
    }

    /**
     * Transforms the meters bound into pixel bounds.
     * Returns a new instance of SphericalBounds in pixels units.
     * @param {Number} zoom the zoom level
     * @return {SphericalBounds}
     */
    toPixel(zoom) {
        return this._convert(zoom, _toPixel, UNITS.pixel);
    }

    /**
     * Transforms the pixel bound into meters bounds.
     * Returns a new instance of SphericalBounds in meters units.
     * @param {Number} zoom the zoom level
     * @return {SphericalBounds}
     */
    toMeter(zoom) {
        return this._convert(zoom, _toMeter, UNITS.meter);
    }

    /**
     * Builds a LatLngBounds from SphericalBounds.
     * The unit can be pixel or meter.
     * @param {Number} [zoom]
     * @returns {woosmap.geometry.LatLngBounds}
     */
    toLatLngBounds(zoom) {
        if (this.unit === UNITS.pixel) {
            if (zoom === undefined) {
                throw "zoom must be supplied in order to convert to latLngBounds";
            }
            return this.toMeter(zoom).toLatLngBounds();
        }
        return new geometry.LatLngBounds(_toLatLng(this.sw), _toLatLng(this.ne));
    }
}

exports.SphericalBounds = SphericalBounds;
exports.fromLatLngBounds = fromLatLngBounds;
exports.converters = converters;
exports.initialResolution = initialResolution;
